import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { AppLayout } from './components/AppLayout'

/**
 * Every module's screens load on demand.
 *
 * This is what keeps the app light as modules are added: somebody who never
 * opens Invoicing never downloads it, so the initial payload stays flat no
 * matter how many modules exist. Bundling everything together would mean each
 * new module taxes every user on every first load.
 *
 * The dashboard is the exception - it is the landing screen, so deferring it
 * would only add a round trip to the one page everyone sees.
 */
import { DashboardPage } from './routes/DashboardPage'
import { LoginPage } from './routes/LoginPage'
import { loadSession } from './session-store'
import { can } from './api/session'
import type { CurrentUser } from './api/types'

type RouterContext = { queryClient: QueryClient }

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>) => ({
    // Where to return to once signed in, so a bookmarked deep link survives
    // an expired session.
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    // Why a Google sign-in was turned away, put there by the server's
    // redirect. A reason code rather than a sentence: the wording belongs to
    // the page, and a message in the address bar is a message anybody can
    // rewrite.
    google_error: typeof search.google_error === 'string' ? search.google_error : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const user = await loadSession(context.queryClient)
    if (user) {
      throw redirect({ to: search.redirect ?? '/' })
    }
  },
  component: LoginPage,
})

/**
 * Everything behind authentication hangs off this pathless route, so the guard
 * is written once and no screen can be added without it.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: async ({ context, location }) => {
    const user = await loadSession(context.queryClient)

    if (!user) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href === '/' ? undefined : location.href,
          google_error: undefined,
        },
      })
    }

    return { user }
  },
  component: AppLayout,
})

/**
 * Refuses a route to somebody who does not hold the permission behind it.
 *
 * **Hiding a link is not a guard.** The navigation already omits screens
 * somebody cannot use, but a link can be pasted, bookmarked, shared in a
 * message or reached with the back button, and none of those go through the
 * navigation. Without this, `/activity` typed into the address bar rendered
 * the page and let the API refuse each request - so the screen appeared, then
 * filled with errors, which reads as broken rather than as forbidden.
 *
 * Sent to the dashboard rather than to a "forbidden" page: every signed-in
 * person can use that one, so it is somewhere to be rather than somewhere
 * else to bounce off. Redirected by `href` because a `to:` inside this file
 * would make the route tree's type depend on the router built from it, and
 * TypeScript gives up and widens every typed path in the file.
 *
 * This is convenience, not security. Every endpoint behind every one of these
 * screens checks again server-side, and that is the check that matters.
 */
function requirePermission(user: CurrentUser | undefined, ...permissions: string[]): void {
  if (permissions.some((permission) => can(user, permission))) return

  throw redirect({ href: '/' })
}

/*
 * The dashboard is not route-guarded, though it does name a permission.
 *
 * `requirePermission` sends somebody to the dashboard, so guarding the
 * dashboard with it would be a redirect loop for exactly the person who holds
 * nothing. The page itself says so instead, which is also the more honest
 * screen: somewhere to be, with an explanation, rather than a bounce.
 */
const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: DashboardPage,
})

const activityRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/activity',
  beforeLoad: ({ context }) => requirePermission(context.user, 'activity_log.read'),
  component: lazyRouteComponent(() => import('./routes/ActivityPage'), 'ActivityPage'),
})

const projectsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/project',
  beforeLoad: ({ context }) => requirePermission(context.user, 'project.read'),
  // Which tab is open, so a link to one survives a refresh and can be
  // pasted. Summary is the default: it is the first tab and the overview.
  validateSearch: (search: Record<string, unknown>): { view: 'all' | 'settings' | 'summary'; section?: 'tags' | 'teams' } => ({
    view:
      search.view === 'all'
        ? ('all' as const)
        : search.view === 'settings'
          ? ('settings' as const)
          : ('summary' as const),
    // Which part of Settings is open, so a link to the teams lands on them.
    ...(search.section === 'tags' || search.section === 'teams' ? { section: search.section } : {}),
  }),
  component: lazyRouteComponent(() => import('./routes/ProjectsPage'), 'ProjectsPage'),
})

const projectBoardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/project/$projectId',
  beforeLoad: ({ context }) => requirePermission(context.user, 'project.read'),
  component: lazyRouteComponent(() => import('./routes/ProjectBoardPage'), 'ProjectBoardPage'),
})

/**
 * A task open over its own board: /project/{id}/task/WEB-42.
 *
 * A child of the board rather than a route of its own, which is the whole
 * point: the board stays mounted underneath, so opening a card is a dialog
 * over the work rather than a page that replaces it - and the address bar
 * still names the task, so back closes it and the URL can be pasted.
 *
 * It renders nothing itself. The board reads `taskKey` from the match and
 * draws the dialog, because the dialog needs the board's data and mutations
 * and passing those down to a sibling route would be the long way round.
 */
const boardTaskRoute = createRoute({
  getParentRoute: () => projectBoardRoute,
  path: 'task/$taskKey',
  component: () => null,
})

/**
 * A task on a page of its own: /project/task/WEB-42.
 *
 * Where every link from outside a board goes. Somebody arriving from a pasted
 * key or a list of their own work has no board behind them, so a dialog would
 * float over nothing and a refresh would have nowhere to put it back.
 */
const taskDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/project/task/$taskKey',
  beforeLoad: ({ context }) => requirePermission(context.user, 'project.read'),
  component: lazyRouteComponent(() => import('./routes/TaskDetailPage'), 'TaskDetailPage'),
})

/**
 * The short form, /task/WEB-42, kept because it is already pasted into
 * messages. It forwards rather than rendering, so there is one task page.
 */
const shortTaskRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/task/$taskKey',
  beforeLoad: ({ params }) => {
    // By href rather than by typed route. A typed `to` here would make the
    // route tree's type depend on the router the tree is being used to
    // build, and TypeScript gives up on the cycle by widening every Link in
    // the app to an unregistered router.
    throw redirect({ href: `/project/task/${params.taskKey}`, replace: true })
  },
})

const subscriptionsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/subscription',
  beforeLoad: ({ context }) => requirePermission(context.user, 'subscription.read'),
  component: lazyRouteComponent(() => import('./routes/SubscriptionsPage'), 'SubscriptionsPage'),
})

/*
  One subscription, by its uuid: /subscription/01a0cab8-...
  A page rather than a panel over the list, so it can be linked to, opened
  in a tab and returned to.
*/
const subscriptionDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/subscription/$subscriptionId',
  beforeLoad: ({ context }) => requirePermission(context.user, 'subscription.read'),
  // Which tab is open, so "Record payment" on the list lands on the payment
  // form rather than on the page's first tab.
  validateSearch: (search: Record<string, unknown>) => ({
    tab:
      search.tab === 'payments' ||
      search.tab === 'timeline' ||
      search.tab === 'overview' ||
      search.tab === 'integration'
        ? (search.tab as 'payments' | 'timeline' | 'overview' | 'integration')
        // Reports is the first tab, so it is also where a bare link lands.
        : ('reports' as const),
  }),
  component: lazyRouteComponent(() => import('./routes/SubscriptionDetailPage'), 'SubscriptionDetailPage'),
})

/*
  Accounts: money in and money out. The overview is its own page; invoices
  and subscriptions keep their addresses and join the section through the
  tab strip, so no link anybody has saved stops working.
*/
const accountsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/accounts',
  beforeLoad: ({ context }) => requirePermission(context.user, 'finance.read', 'finance.gst.read', 'subscription.read'),
  component: lazyRouteComponent(() => import('./routes/AccountsPage'), 'AccountsPage'),
})

/* GST by month: what is owed to the government for each month's invoices, and whether it has been paid. */
const gstRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/accounts/gst',
  beforeLoad: ({ context }) => requirePermission(context.user, 'finance.gst.read'),
  component: lazyRouteComponent(() => import('./routes/FinanceRoutes'), 'GatedGstPage'),
})

/* Payments recorded from a bank statement, read in the browser. */
const bankRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/accounts/bank',
  beforeLoad: ({ context }) => requirePermission(context.user, 'finance.manage'),
  component: lazyRouteComponent(() => import('./routes/FinanceRoutes'), 'GatedBankPage'),
})

const invoicesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/invoice',
  beforeLoad: ({ context }) => requirePermission(context.user, 'finance.read'),
  component: lazyRouteComponent(() => import('./routes/FinanceRoutes'), 'GatedInvoicesPage'),
})

/*
  One invoice, by its uuid rather than its number: a draft has no number
  yet, and the address has to work from the moment it is started.
*/
const invoiceDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/invoice/$invoiceId',
  beforeLoad: ({ context }) => requirePermission(context.user, 'finance.read'),
  component: lazyRouteComponent(() => import('./routes/FinanceRoutes'), 'GatedInvoiceDetailPage'),
})

/* The invoice as it is sent, for Print and "Save as PDF". */
const invoicePrintRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/invoice/$invoiceId/print',
  beforeLoad: ({ context }) => requirePermission(context.user, 'finance.read'),
  component: lazyRouteComponent(() => import('./routes/FinanceRoutes'), 'GatedInvoicePrintPage'),
})

const profileRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/profile',
  component: lazyRouteComponent(() => import('./routes/ProfilePage'), 'ProfilePage'),
})

const toolsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tool',
  beforeLoad: ({ context }) => requirePermission(context.user, 'tool.read'),
  component: lazyRouteComponent(() => import('./routes/ToolsPage'), 'ToolsPage'),
})

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  beforeLoad: ({ context }) => requirePermission(context.user, 'settings.access'),
  component: lazyRouteComponent(() => import('./routes/SettingsPage'), 'SettingsPage'),
})

/*
  One route per settings group, rather than one page with a tab per thing.
  A group is a destination somebody can link to and come back to, and the
  next group is a route beside this one rather than another tab squeezed
  into a strip that is already full.
*/
/*
 * Users and Roles are two addresses, not one address with a tab in component
 * state.
 *
 * A tab held in state is invisible to the browser: refreshing lands back on
 * the first one, the back button leaves the page entirely, and a link to
 * "the roles screen" cannot be sent to anybody. Making each tab a route fixes
 * all three at once, and the strip becomes navigation rather than a widget
 * that happens to swap panels.
 */
const settingsUsersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/users',
  // Either is enough: the page shows the tabs the viewer can actually use.
  beforeLoad: ({ context }) =>
    requirePermission(context.user, 'settings.access', 'user.read', 'user.role.read'),
  component: lazyRouteComponent(() => import('./routes/SettingsPeoplePage'), 'SettingsUsersPage'),
})

/*
 * Nested under users, because that is where it lives.
 *
 * Roles are a view of the Users screen rather than a destination beside it,
 * and the address says so: somebody trimming back to /settings/users lands
 * on the screen this tab belongs to rather than on a sibling.
 */
const settingsRolesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/users/roles',
  beforeLoad: ({ context }) =>
    requirePermission(context.user, 'settings.access', 'user.read', 'user.role.read'),
  component: lazyRouteComponent(() => import('./routes/SettingsPeoplePage'), 'SettingsRolesPage'),
})

/*
 * Teams moved to the Projects screen's Settings tab, because all a team does
 * is open project boards. The old address forwards, so a saved link still
 * lands on them. By href: a typed redirect here is circular (see AGENTS.md).
 */
const settingsTeamsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/users/teams',
  beforeLoad: () => {
    throw redirect({ href: '/project?view=settings&section=teams' })
  },
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([
    accountsRoute,
    gstRoute,
    bankRoute,
    invoicesRoute,
    invoiceDetailRoute,
    invoicePrintRoute,
    subscriptionsRoute,
    subscriptionDetailRoute,
    dashboardRoute,
    projectsRoute,
    projectBoardRoute.addChildren([boardTaskRoute]),
    // Before the board's own path in the tree for readability only; the
    // router matches on specificity, and "task" is a literal segment where
    // $projectId is a parameter.
    taskDetailRoute,
    shortTaskRoute,
    activityRoute,
    toolsRoute,
    settingsRoute,
    settingsUsersRoute,
    settingsRolesRoute,
    settingsTeamsRoute,
    profileRoute,
  ]),
])

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>
  }
}
